import { ReferralScheme } from './dbShared/dbEnums';

/**
 * Default monthly spend limit.
 * Equals to (maxed out weekly subscription price x 5 weeks).
 * Can be overridden on a player-by-player basis via the spendLimitPence field on the player table.
 */
export const DEFAULT_SPEND_LIMIT_FIAT_AMOUNT_IN_PENCE = 12000;

/** The bucket name for Prize Assets. This name is also used to build S3 bucket URLs */
export const S3_BUCKET_NAME_PRIZE_ASSETS = 'daymade-prize-assets';
/** The bucket name for Leaderboard Assets. This name is also used to build S3 bucket URLs */
export const S3_BUCKET_NAME_LEADERBOARD_ASSETS =
  'triphunters-leaderboard-photos';

export const AUTOFULFILMENT_CODES = {
  DAYMADE_TOKEN: 'DAYMADE_TOKEN',
  TREE: 'TREE',
  MAIN_DRAW_PREMIUM_ENTRY: 'MAIN_DRAW_PREMIUM_ENTRY',
  GIFTCLOUD_REWARD: 'GIFTCLOUD_REWARD',
} as const;

/** Number of days players have to claim their prizes */
export const PRIZE_WIN_VALIDITY_IN_DAYS = 28;

/** A mapping of prize tiers codes to human-friendly names */
export const PrizeTierMap = {
  Treat1: 'Treat 1',
  Treat2: 'Treat 2',
  Treat3: 'Treat 3',
  Bronze: 'Bronze prize',
  Silver: 'Silver prize',
  Gold: 'Gold prize',
  Platinum: 'Platinum prize',
  Diamond: 'Diamond prize',
  DAYMAKER: 'DAYMAKER prize',
  DAYMAKERboost: 'DAYMAKER+ prize',
} as const;

export const TreatPrizeTierCodes: ReadonlyArray<keyof typeof PrizeTierMap> = [
  'Treat1',
  'Treat2',
  'Treat3',
] as const;

export const Monthly10k = {
  /** The URL path for the monthly 10k draws, intended to be used to link  */
  urlPath: '/monthly-10k-giveaway',
  abTestName: 'MonthlyGiveaway',
  abTestValue: 'YES',
  drawTypeCode: 'MONTHLY_GIVEAWAY',
  drawTypePlanCode: 'MONTHLY_GIVEAWAY',
  drawTypePlanSubcode: 'BASE',
  /** The number of entries earnt per week. Note that users may be able to earn
   * additional entries for other actions, which are not included in this value
   **/
  minimumEntriesPerWeek: 5,
  /** The days of the month that entries are awarded for February */
  entryAwardDayOfMonthFeb: [1, 8, 15, 21, 28],
  /** The days of the month that entries are awarded for other months */
  entryAwardDayOfMonthOther: [1, 8, 15, 21, 29],
} as const;

export const TreatWinEmails = {
  abTestName: 'TreatWinEmails',
  abTestValue: 'TEST',
} as const;

export const BUSINESS_TIME_ZONE = 'Europe/London';

export const MAIN_PRIZE_STRUCTURE_CODE = 'DMDP';

/** Whether to use SSL for the API when running in local environment. See notes
 * on this page to see why we would want to do that:
 * https://www.notion.so/daymadeuk/Testing-on-other-devices-30c23e7cd84080a2b78bd8caf59d9b9c */
export const USE_SSL_API_IN_DEV = false;

export const PASSWORD_RESET_VALIDITY_HOURS = 24;

export const NEW_MASCOTS = {
  1: 'https://daymade-website-assets.s3.amazonaws.com/2026/mascots/Mascot1.png',
  2: 'https://daymade-website-assets.s3.amazonaws.com/2026/mascots/Mascot2.png',
  3: 'https://daymade-website-assets.s3.amazonaws.com/2026/mascots/Mascot3.png',
  4: 'https://daymade-website-assets.s3.amazonaws.com/2026/mascots/Mascot4.png',
} as const;

export const IntegratedGiftCardProviders = ['GIFTCLOUD'] as const;
export type TIntegratedGiftCardProviders =
  (typeof IntegratedGiftCardProviders)[number];
export const IntegratedGiftCardMap = {
  GIFTCLOUD: {
    5: '£5',
    10: '£10',
    15: '£15',
    20: '£20',
    25: '£25',
    30: '£30',
    50: '£50',
    100: '£100',
    150: '£150',
    300: '£300',
    500: '£500',
    wwf5: '£5 (Win With Friends)',
    wwf15: '£15 (Win With Friends)',
    wwf200: '£200 (Win With Friends)',
    wwf500: '£500 (Win With Friends)',
    wwf1000: '£1000 (Win With Friends)',
    wwf5000: '£5000 (Win With Friends)',
  },
} as const satisfies Record<
  TIntegratedGiftCardProviders,
  Record<string, string>
>;

export const ACTIVE_REFERRAL_SCHEME_CODE = ReferralScheme.WINWITHFRIENDS;

/** Maximum number of friends a player can refer (outgoing invites) */
export const MAX_REFERRALS = 3;

/** The list of prize badges that are supported right now. See prizeBadgesForCards in the front end for how they are rendered */
export const ALL_PRIZE_BADGES = ['LASTMINUTE'] as const;
export type ALL_PRIZE_BADGES = (typeof ALL_PRIZE_BADGES)[number];
